import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Sparkles, Loader } from "lucide-react";
import EditorialProgressMap from "@/components/EditorialProgressMap";
import api from "@/lib/api";
import "./PathwayPrint.css";

/**
 * PathwayPrint (iter73 + iter74 Phase C)
 *
 * A4-landscape single-page print report:
 *   · Header (student · target · coach · date)
 *   · Helter Skelter storyboard (SVG + zone cards)
 *   · Starting vs Current stats table (iter74 Phase C)
 *   · AI Vision · Claude-drafted 30-day plan + recommended sessions (iter74)
 *   · Coach / Player / Date signature footer
 *
 * The route always forces `data-theme="light"` while mounted so users on
 * Elite Performance dark still get a print-friendly report.
 */
export default function PathwayPrint() {
  const { studentId } = useParams();
  const [params] = useSearchParams();
  const sport = params.get("sport") || "pickleball";
  const variant = params.get("variant") || "flat_sporty";
  const [meta, setMeta] = useState(null);
  const [starting, setStarting] = useState(null);
  const [vision, setVision] = useState(null);
  const [visionLoading, setVisionLoading] = useState(false);
  const [visionError, setVisionError] = useState(null);
  const [visionHistory, setVisionHistory] = useState([]);

  // Force light mode for the whole print page and restore on unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "light");
    return () => {
      if (prev) document.documentElement.setAttribute("data-theme", prev);
      else document.documentElement.removeAttribute("data-theme");
    };
  }, []);

  // Load pathway + starting snapshot in parallel.
  useEffect(() => {
    if (!studentId) return;
    api.get(`/students/${studentId}/pathway`, { params: { sport } })
      .then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
    api.get(`/students/${studentId}/pathway/starting`)
      .then((r) => setStarting(r.data))
      .catch(() => setStarting(null));
  }, [studentId, sport]);

  // AI Vision — fetched on mount (cached server-side). Force a refresh
  // when the coach explicitly asks.
  const fetchVision = async (forceRefresh = false) => {
    setVisionLoading(true);
    setVisionError(null);
    try {
      const { data } = await api.post(
        `/students/${studentId}/pathway/vision-plan`,
        { sport, force_refresh: !!forceRefresh },
      );
      setVision(data);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      // Backend validation errors come back as an array of Pydantic error
      // objects — coerce to a readable string so React can render it.
      const msg = Array.isArray(raw)
        ? raw.map((r) => r?.msg || JSON.stringify(r)).join("; ")
        : (typeof raw === "string" ? raw : "Could not generate a plan.");
      setVisionError(msg);
    } finally { setVisionLoading(false); }
  };
  useEffect(() => { if (studentId) fetchVision(false);   }, [studentId, sport]);

  // Iter78: pull the recent-plans list so students can revisit previous AI
  // Vision drafts (e.g. compare last month's plan to this month's).
  useEffect(() => {
    if (!studentId) return;
    api.get(`/students/${studentId}/pathway/vision-history`, { params: { sport, limit: 6 } })
      .then((r) => setVisionHistory(r.data?.items || []))
      .catch(() => setVisionHistory([]));
  }, [studentId, sport, vision]);

  const plan = vision?.plan;

  return (
    <div className="helter-print-page" data-testid="pathway-print-page">
      <header className="helter-print-header">
        <div className="brand">RALLY LAB · PICKLEBALL PATHWAY</div>
        <div className="student-meta">
          <div className="student-name">{meta?.student?.name || "Player"}</div>
          <div className="student-sub">
            {meta?.student?.current_level && (
              <span>Target level <strong>{meta.student.current_level}</strong> · </span>
            )}
            {meta?.target_level && (
              <span>Next level <strong>{meta.target_level}</strong></span>
            )}
          </div>
        </div>
        <div className="issued">
          Issued <strong>{new Date().toLocaleDateString("en-GB")}</strong>
          {meta?.coach?.name && <span> by <strong>{meta.coach.name}</strong></span>}
        </div>
      </header>

      <main className="helter-print-body">
        <EditorialProgressMap
          studentId={studentId}
          sport={sport}
          showPrintButton={false}
          printMode
        />

        {/* --- Starting vs Current stats table --- */}
        {starting && starting.axes && starting.axes.length > 0 && (
          <section className="helter-print-block" data-testid="pathway-print-starting-block">
            <div className="block-eyebrow">STARTING VS CURRENT</div>
            <div className="block-title">How far you&apos;ve come.</div>
            {starting.starting_at && (
              <div className="block-sub">Baseline captured {new Date(starting.starting_at).toLocaleDateString("en-GB")}</div>
            )}
            <table className="compare-table">
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Starting</th>
                  <th>Current</th>
                  <th>Δ</th>
                </tr>
              </thead>
              <tbody>
                {starting.axes.map((r) => {
                  const gainClass = r.delta == null ? "" : r.delta > 0 ? "gain-up" : r.delta < 0 ? "gain-down" : "gain-flat";
                  return (
                    <tr key={r.axis}>
                      <td>{r.axis}</td>
                      <td>{r.starting == null ? "—" : Number(r.starting).toFixed(2)}</td>
                      <td>{r.current == null ? "—" : Number(r.current).toFixed(2)}</td>
                      <td className={gainClass}>
                        {r.delta == null ? "—" : (r.delta > 0 ? "+" : "") + r.delta.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {/* --- AI Vision --- */}
        <section className="helter-print-block" data-testid="pathway-print-vision-block">
          <div className="block-eyebrow"><Sparkles className="w-3 h-3 inline-block mr-1" />AI VISION · YOUR NEXT 30 DAYS</div>
          {visionLoading && (
            <div className="vision-loading">
              <Loader className="w-4 h-4 animate-spin inline-block mr-2" />
              Drafting your plan with Claude…
            </div>
          )}
          {visionError && (
            <div className="vision-error">
              {visionError} <button onClick={() => fetchVision(true)} className="link">Try again</button>
            </div>
          )}
          {!visionLoading && plan && (
            <div>
              {plan.headline && <div className="block-title">{plan.headline}</div>}
              {plan.recommended_sessions && (
                <div className="recommended-sessions">
                  <span>Coach recommends</span>
                  <strong>{plan.recommended_sessions.count ?? "—"} sessions</strong>
                  <span>&middot; {plan.recommended_sessions.cadence || "—"}</span>
                  {plan.recommended_sessions.notes && (
                    <div className="notes">{plan.recommended_sessions.notes}</div>
                  )}
                </div>
              )}
              {Array.isArray(plan.weeks) && plan.weeks.length > 0 && (
                <div className="weeks-grid">
                  {plan.weeks.map((w) => (
                    <div key={w.week} className="week-cell">
                      <div className="week-num">Week {w.week}</div>
                      <div className="week-focus">{w.focus}</div>
                      {Array.isArray(w.drills) && (
                        <ul>
                          {w.drills.map((d, i) => (<li key={i}>{d}</li>))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(plan.recommended_offerings) && plan.recommended_offerings.length > 0 && (
                <div className="recommended-classes">
                  <div className="rc-title">Book these classes next:</div>
                  <ul>
                    {plan.recommended_offerings.map((o) => (
                      <li key={o.id || o.title}>
                        <strong>{o.title || "Upcoming class"}</strong>
                        {o.starts_at && (
                          <span> · {new Date(o.starts_at).toLocaleDateString("en-GB")}</span>
                        )}
                        {o.why && <span> — {o.why}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {plan._parse_error && (
                <div className="vision-error">Claude replied in an unexpected shape. <button onClick={() => fetchVision(true)} className="link">Regenerate</button></div>
              )}
            </div>
          )}

          {/* Iter78: recent Vision plans (history) */}
          {visionHistory.length > 1 && (
            <div className="vision-history" data-testid="pathway-vision-history">
              <div className="vision-history__hdr">EARLIER PLANS</div>
              <ul>
                {visionHistory.slice(1).map((h) => (
                  <li key={h.id}>
                    <span className="date">{new Date(h.created_at).toLocaleDateString("en-GB")}</span>
                    <span className="headline">{h.headline || "(no headline)"}</span>
                    {h.recommended_sessions?.count && (
                      <span className="sessions">{h.recommended_sessions.count} sessions</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
        {/* --- Signature footer — sits at the very bottom, after the roadmap
            + starting-vs-current + AI Vision blocks (iter74 Phase C). --- */}
        <div className="helter-print-signature" data-testid="pathway-print-signature">
          <div className="sig-block">
            <div className="sig-line" />
            <div className="sig-label">Coach signature</div>
            <div className="sig-name">{meta?.coach?.name || "Coach"}</div>
          </div>
          <div className="sig-block">
            <div className="sig-line" />
            <div className="sig-label">Player signature</div>
            <div className="sig-name">{meta?.student?.name || "Player"}</div>
          </div>
          <div className="sig-block">
            <div className="sig-line" />
            <div className="sig-label">Date</div>
            <div className="sig-name">{new Date().toLocaleDateString("en-GB")}</div>
          </div>
        </div>
      </main>

      <div className="helter-print-toolbar">
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="pathway-print-btn"
        >Print this pathway</button>
        <button
          type="button"
          onClick={() => fetchVision(true)}
          data-testid="pathway-vision-refresh"
          className="secondary"
        >Regenerate AI Vision</button>
        <span className="tip">Tip: choose <em>Landscape</em> in your browser&apos;s print dialog for the best fit.</span>
      </div>
    </div>
  );
}
